import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Download, CheckCircle2, Clock, BarChart3, ListChecks, ClipboardList } from "lucide-react";
import { vendorBySlug, getBrochureUrl } from "@/lib/vendors";
import { PARTNER_LOGOS } from "@/lib/data";
import BgLayer from "@/components/ui-shared/BgLayer";
import BrochureModal from "@/components/ui-shared/BrochureModal";

const Section = ({ icon: Icon, title, children }) => (
    <div className="sxe-card p-6">
        <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#A100FF]/25 to-[#7B00CC]/5 border border-[#C266FF]/25 flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-[#E9CCFF]" />
            </div>
            <h2 className="font-display text-white text-base font-medium tracking-tight">{title}</h2>
        </div>
        <div className="mt-4">{children}</div>
    </div>
);

const CourseDetailPage = () => {
    const { slug, courseId } = useParams();
    const vendor = vendorBySlug(slug);
    const [modalOpen, setModalOpen] = useState(false);
    if (!vendor) return <Navigate to="/partners" replace />;
    const course = vendor.courses.find((c) => String(c.id) === String(courseId));
    if (!course) return <Navigate to={`/partners/${slug}`} replace />;

    const logo = PARTNER_LOGOS[vendor.slug];
    const d = course.details || {};
    const brochureUrl = vendor.brochureFiles ? getBrochureUrl(vendor, course.title) : null;

    return (
        <div data-testid={`course-detail-page-${course.id}`}>
            <section className="relative pt-32 md:pt-40 pb-14 overflow-hidden">
                <BgLayer variant="mesh" intensity="strong" />
                <div
                    className="absolute inset-0 opacity-30"
                    style={{ backgroundImage: `linear-gradient(135deg, ${vendor.hue}22 0%, transparent 60%)` }}
                />
                <div className="sxe-container relative grid lg:grid-cols-[1.4fr_1fr] gap-12 items-center">
                    <div>
                        <Link to={`/partners/${slug}`} className="inline-flex items-center gap-1.5 text-[#B0A3C4] hover:text-white text-sm font-display">
                            <ArrowLeft className="w-3.5 h-3.5" /> Back to {vendor.name}
                        </Link>
                        <div className="mt-4 overline">
                            <span className="dot" /> {d.format || "Course"}{course.category ? ` · ${course.category}` : ""}
                        </div>
                        <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tighter leading-[1.1] text-white mt-4">
                            {course.title}
                        </h1>
                        {d.description && (
                            <p className="text-[#B0A3C4] text-[15px] leading-relaxed mt-4 max-w-xl">{d.description}</p>
                        )}

                        <div className="mt-6 flex flex-wrap items-center gap-2">
                            {d.level && <span className="sxe-chip inline-flex items-center gap-1.5"><BarChart3 className="w-3.5 h-3.5" /> {d.level}</span>}
                            {d.duration && <span className="sxe-chip inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {d.duration}</span>}
                        </div>

                        {brochureUrl && (
                            <button
                                type="button"
                                onClick={() => setModalOpen(true)}
                                data-testid={`course-detail-download-brochure-${course.id}`}
                                className="btn-primary mt-7"
                            >
                                Download Brochure <Download className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    {logo && (
                        <div
                            className="relative rounded-2xl overflow-hidden border border-[#C266FF]/20 bg-white p-0 h-[280px] md:h-[320px] flex items-center justify-center"
                            style={{ borderTop: `4px solid ${vendor.hue}` }}
                            data-testid="course-detail-vendor-logo"
                        >
                            <img
                                src={logo}
                                alt={`${vendor.name} logo`}
                                className="max-w-[68%] max-h-[54%] object-contain"
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                        </div>
                    )}
                </div>
            </section>

            <section className="sxe-section relative bg-[#08031a]">
                <BgLayer variant="dots" />
                <div className="sxe-container relative">
                    <div className="grid sm:grid-cols-2 gap-5">
                        {d.objectives?.length > 0 && (
                            <Section icon={ListChecks} title="What you'll learn">
                                <ul className="space-y-2.5">
                                    {d.objectives.map((o, i) => (
                                        <li key={i} className="flex items-start gap-2.5 text-[#E9CCFF]">
                                            <CheckCircle2 className="w-4 h-4 text-[#C266FF] shrink-0 mt-0.5" />
                                            <span className="text-[13.5px] leading-relaxed">{o}</span>
                                        </li>
                                    ))}
                                </ul>
                            </Section>
                        )}

                        {d.prerequisites?.length > 0 && (
                            <Section icon={ListChecks} title="Prerequisites">
                                <ul className="space-y-2.5">
                                    {d.prerequisites.map((p, i) => (
                                        <li key={i} className="text-[#B0A3C4] text-[13.5px] leading-relaxed flex items-start gap-2.5">
                                            <span className="text-[#C266FF] mt-0.5">•</span> {p}
                                        </li>
                                    ))}
                                </ul>
                            </Section>
                        )}

                    </div>

                    {d.outline?.length > 0 && (
                        <div className="mt-10">
                            <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#A100FF]/25 to-[#7B00CC]/5 border border-[#C266FF]/25 flex items-center justify-center shrink-0">
                                    <ClipboardList className="w-4 h-4 text-[#E9CCFF]" />
                                </div>
                                <h2 className="font-display text-white text-xl font-medium tracking-tight">Course outline</h2>
                            </div>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                                {d.outline.map((item, i) => (
                                    <div key={i} className="sxe-card p-5">
                                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#A100FF]/25 to-[#7B00CC]/5 border border-[#C266FF]/25 flex items-center justify-center shrink-0">
                                            <span className="font-mono text-[12px] text-[#E9CCFF]">0{i + 1}</span>
                                        </div>
                                        <h3 className="font-display text-white text-[14.5px] font-medium tracking-tight leading-snug mt-4">
                                            {typeof item === "string" ? item : item.title}
                                        </h3>
                                        {item?.description && (
                                            <p className="text-[#B0A3C4] text-[12.5px] leading-relaxed mt-2">{item.description}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <BrochureModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                courseTitle={course.title}
                courseId={course.id}
                vendorSlug={vendor.slug}
                vendorName={vendor.name}
                brochureUrl={brochureUrl}
            />
        </div>
    );
};

export default CourseDetailPage;
